import * as React from 'react';
import { DashboardDataTable } from '@shigo-ui/dashboard-ui';

import { dashboardErrorMessage } from '../../../../../lib/apiErrorMessages';
import { dashboardT } from '../../../../../lib/messages';
import type { AssignedAgency } from '../../../../auth/agencyAccess';
import { ShigoPermission } from '../../../../auth/permissions';
import { useShigoPermissions } from '../../../../auth/useShigoPermissions';
import { useAgencyCampaignContext } from '../../../agencyCampaignContext';
import { BrandEditorDialog } from '../BrandEditorDialog/BrandEditorDialog';
import { useIndustriesQuery } from '../../hooks';
import type { Brand } from '../../model';
import { resolveBrandEditorAgency } from './brandsTableData';
import { buildBrandsTableColumns } from './brandsTableColumns';
import { BrandsTableToolbar } from './BrandsTableToolbar';
import { useBrandsTableRows } from './useBrandsTableRows';

export function BrandsTable({
  agencyByBrandId: providedAssignments,
  parentAgency,
}: {
  agencyByBrandId?: ReadonlyMap<string, AssignedAgency>;
  parentAgency?: AssignedAgency | undefined;
} = {}) {
  const scope = useAgencyCampaignContext();
  const rows = useBrandsTableRows(parentAgency, providedAssignments);
  const { setAgencies, filters, setIndustries, setPagination, setSearch } = rows.routeFilters;
  const { can } = useShigoPermissions();
  const canWrite = Boolean(scope) || can(ShigoPermission.MadBuyCampaignWrite);
  const [editingBrand, setEditingBrand] = React.useState<Brand | null | undefined>();
  const industries = useIndustriesQuery();
  const agencyIds = filters.agencyIds ?? [];

  const columns = buildBrandsTableColumns({
    scopeAgency: scope?.agency,
    parentAgency,
    agencyByBrandId: rows.agencyByBrandId,
    canWrite,
    onEdit: setEditingBrand,
  });

  const Container = parentAgency ? 'div' : 'main';
  return (
    <Container className={parentAgency ? 'min-w-0' : 'page-gutter min-w-0 flex-1 py-4 md:py-6'}>
      <BrandsTableNotices
        assignmentsError={rows.assignmentsError}
        agencyFilterActive={agencyIds.length > 0}
        industriesError={industries.isError ? industries.error : null}
      />
      <DashboardDataTable
        title={dashboardT('brands.title')}
        tableLabel={dashboardT('brands.title')}
        columns={columns}
        items={rows.items}
        getRowKey={(brand) => brand.id}
        loading={rows.loading}
        loadingMessage={dashboardT('brands.loading')}
        emptyMessage={dashboardT('brands.empty')}
        errorMessage={rows.failed ? dashboardT('brands.loadError') : undefined}
        pagination={{
          value: {
            page: filters.page,
            pageSize: filters.size,
            totalItems: rows.totalItems,
          },
          pageSizes: [5, 10, 25, 50, 100],
          disabled: rows.loading,
          onChange: (next) => {
            setPagination(next.page, next.pageSize);
          },
        }}
        toolbar={
          <BrandsTableToolbar
            showAgencyFilter={!scope}
            agencyIds={agencyIds}
            onAgenciesChange={setAgencies}
            search={filters.name}
            onSearchChange={setSearch}
            industryOptions={industries.data ?? []}
            selectedIndustries={filters.industries}
            industriesLoading={industries.isLoading}
            onIndustriesChange={setIndustries}
            onCreate={
              canWrite
                ? () => {
                    setEditingBrand(null);
                  }
                : undefined
            }
          />
        }
      />
      {canWrite && editingBrand !== undefined ? (
        <BrandEditorDialog
          brand={editingBrand}
          parentAgency={resolveBrandEditorAgency(
            editingBrand,
            parentAgency,
            scope?.agency,
            rows.agencyByBrandId,
          )}
          onClose={() => {
            setEditingBrand(undefined);
          }}
        />
      ) : null}
    </Container>
  );
}

function BrandsTableNotices({
  assignmentsError,
  agencyFilterActive,
  industriesError,
}: {
  assignmentsError: unknown;
  agencyFilterActive: boolean;
  industriesError: unknown;
}) {
  return (
    <>
      {assignmentsError ? (
        <p role="alert" className="text-destructive">
          {dashboardErrorMessage(assignmentsError)}
        </p>
      ) : null}
      {agencyFilterActive ? (
        <p role="status">{dashboardT('agencyDemand.adminFilterUnavailable')}</p>
      ) : null}
      {industriesError ? (
        <p role="alert" className="text-destructive">
          {dashboardErrorMessage(industriesError)}
        </p>
      ) : null}
    </>
  );
}
